import { useState, useCallback, useEffect } from 'react';
import { Box, Tabs, Tab, Button } from '@mui/material';
import AddTreatmentForm from './AddTreatmentForm';
import TreatmentHistory from './TreatmentHistory';
import { ncdAPI } from '../../../../services/ncd.api';
import './HypertensionFollowup.css';

const HypertensionFollowup = ({ individualId, htnScreenId, setShowHypertensionFollowup, onSubmit }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [treatmentHistory, setTreatmentHistory] = useState([]);
  const [loadingTreatment, setLoadingTreatment] = useState(true);
  const [status, setStatus] = useState("");

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleClose = useCallback(() => {
    setShowHypertensionFollowup(false);
  }, [setShowHypertensionFollowup]);

  const formatDiagnosis = (diag) => {
    if (!diag) return "-";

    if (diag.includes("STAGE_1")) return "Hypertension Stage 1";
    if (diag.includes("STAGE_2")) return "Hypertension Stage 2";
    if (diag.includes("NORMAL")) return "Normal";

    return diag.replaceAll("_", " ");
  };

  // ✅ Transform API → UI
  const formatData = useCallback((apiData = []) => {
    return apiData
      .sort((a, b) => b.treatDateWithTimeStamp - a.treatDateWithTimeStamp)
      .map((item) => {
        // Parse followUpDuration "28;0;0" format (days;weeks;months)
        const durationParts = item.docfolUp?.followUpDuration?.split(';') || ['0', '0', '0'];
        const days = durationParts[0] || '0';
        const weeks = durationParts[1] || '0';
        const months = durationParts[2] || '0';

        return {
          date: item.treatDate,
          facility: item.facility,
          bp: `${item.sys}/${item.diast}`,
          diagnosis: formatDiagnosis(item.dgns?.diagRresult),
          medicines: item.selMeds?.map((m) => ({
            name: m.name.trim(),
            frequency: m.freq || '-',
            duration: m.dur || '-',
            quantity: m.quan || '-'
          })) || [],
          followUp: {
            duration: `${days} days`,
            reason: item.docfolUp?.followUpReason || 'Review',
            dueDate: item.docfolUp?.followUpDueDate || '-',
            days: days,
            weeks: weeks,
            months: months
          }
        };
      });
  }, []);

  const fetchTreatmentHistory = useCallback(async () => {
    if (!individualId || !htnScreenId) return;

    try {
      setLoadingTreatment(true);

      const res = await ncdAPI.getTreatmentHistory(
        500,
        individualId,
        htnScreenId
      );

      const apiData = res?.data?.treatmentHistory || [];
      setTreatmentHistory(formatData(apiData));

      // call actionDetails
      const respaction = await ncdAPI.actionDetails(
        500,
        individualId,
        htnScreenId
      );

      if (respaction?.data?.curActName) {
        setStatus(respaction?.data?.curActName);
      }
    } catch (error) {
      console.error("Error fetching treatment history:", error);
    } finally {
      setLoadingTreatment(false);
    }
  }, [individualId, htnScreenId, formatData]);

  useEffect(() => {
    fetchTreatmentHistory();
  }, [fetchTreatmentHistory]);

  const handleFormSubmit = useCallback((values) => {
    fetchTreatmentHistory();
    onSubmit?.(values);
  }, [fetchTreatmentHistory, onSubmit]);

  return (
    <Box className="hypertension-followup-container">
        <h3 className='followup-container-title'>Hypertension Followup</h3>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        className="followup-tabs"
      >
        <Tab label="Add Treatment/Follow-up" />
        <Tab label="Treatment History" />
      </Tabs>
      <Box className="tab-content-wrapper">
        {activeTab === 0 && (
          <AddTreatmentForm 
            individualId={individualId} 
            htnScreenId={htnScreenId} 
            onSubmit={handleFormSubmit} 
            treatmentHistory={treatmentHistory}
            loadingTreatment={loadingTreatment}
            onCancel={handleClose}
          />
        )}
        {activeTab === 1 && (
          <TreatmentHistory 
            treatmentData={treatmentHistory}
            loading={loadingTreatment}
            status={status}
          />
        )}
      </Box>
       <Button
        onClick={handleClose}
        sx={{
          border: "2px solid #A9A9A9",
          width: "30px",
          height: "30px",
          borderRadius: "100%",
          cursor: "pointer",
          position: "absolute",
          right: "-8px",
          top: "-10px",
          background: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "auto",
          p: 0,
          "&:hover": {
            backgroundColor: "rgba(0,0,0,0.05)",
          },
        }}
      >
        <img src={"/images/closing.svg"} alt="Close" style={{ width: "16px" }} />
      </Button>
    </Box>
  );
};

export default HypertensionFollowup;
